import { Link, useNavigate, useParams } from 'react-router-dom'
import { DataTable, StatusBadge, EmptyState, Skeleton, Button } from '@rb/ui'
import type { DataTableColumn } from '@rb/ui'
import type { ProjectStatus } from '@rb/ui'
import { useFetch } from '../hooks/useFetch'
import styles from './AdminUserDetail.module.css'

interface AdminUser {
  id: string
  name: string | null
  email: string
  role: string
  created_at: string
  verified_at: string | null
}

interface UserProject {
  id: string
  name: string
  status: string
  updated_at: string
  file_count: number
}

interface AdminUserDetailResponse {
  user: AdminUser
  projects: UserProject[]
}

function formatDate(s: string) {
  const d = new Date(s)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function roleLabel(role: string) {
  if (role === 'super_admin') return 'Super Admin'
  if (role === 'admin') return 'Admin'
  return 'Kunde'
}

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, loading, error } = useFetch<AdminUserDetailResponse>(`/admin/users/${id}`)

  const projectColumns: DataTableColumn<UserProject>[] = [
    {
      key: 'name',
      header: 'Projekt',
      render: (row) => <span className={styles.projectName}>{row.name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status as ProjectStatus} />,
      width: '180px',
    },
    {
      key: 'file_count',
      header: 'Dateien',
      render: (row) => <span className={styles.meta}>{row.file_count}</span>,
      width: '80px',
    },
    {
      key: 'updated_at',
      header: 'Letzte Änderung',
      render: (row) => <span className={styles.meta}>{formatDate(row.updated_at)}</span>,
      width: '160px',
    },
  ]

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton className={styles.skeletonBanner} />
        <Skeleton className={styles.skeletonCard} />
        <Skeleton className={styles.skeletonCard} />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          <p>Benutzer konnte nicht geladen werden.</p>
          <Button variant="secondary" onClick={() => navigate('/admin/users')}>
            Zurück zur Benutzerliste
          </Button>
        </div>
      </div>
    )
  }

  const { user, projects } = data
  const displayName = user.name || user.email

  return (
    <div className={styles.page}>
      {/* View-as banner */}
      <div className={styles.banner}>
        <span className={styles.bannerText}>
          Sie betrachten das Konto von <strong>{displayName}</strong>
        </span>
        <Link to="/admin/users" className={styles.bannerExit}>
          ← Zurück zur Benutzerliste
        </Link>
      </div>

      {/* Account info */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Kontodaten</h2>
        <dl className={styles.infoGrid}>
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>Name</dt>
            <dd className={styles.infoValue}>{user.name || '—'}</dd>
          </div>
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>E-Mail</dt>
            <dd className={styles.infoValue}>{user.email}</dd>
          </div>
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>Rolle</dt>
            <dd className={styles.infoValue}>{roleLabel(user.role)}</dd>
          </div>
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>Erstellt</dt>
            <dd className={styles.infoValue}>{formatDate(user.created_at)}</dd>
          </div>
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>E-Mail verifiziert</dt>
            <dd className={styles.infoValue}>
              {user.verified_at ? formatDate(user.verified_at) : 'Noch nicht verifiziert'}
            </dd>
          </div>
        </dl>
      </section>

      {/* Projects */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Projekte ({projects.length})</h2>
        {projects.length === 0 ? (
          <p className={styles.emptyProjects}>Dieser Benutzer hat noch keine Projekte erstellt.</p>
        ) : (
          <DataTable
            columns={projectColumns}
            rows={projects}
            getRowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/admin/projects/${row.id}`)}
          />
        )}
      </section>
    </div>
  )
}
